// Angular Files
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// Payment Integration Files
import {
 PaymentMethodData, IFramePaymentResponse, IFrameSavePaymentMethodResponse, PaymentMethodTypeEnumConvertor, PaymentMethodTypeEnum
} from 'apps/public-portal/src/app/payment-integrations/base/models';
// For some reason if the PaymentProcessorService was included from just base, the unit tests would fail
import { PaymentProcessorService } from 'apps/public-portal/src/app/payment-integrations/base/interfaces';

// Teller Online Files
import {
    IFramePaymentRequestDto,
    IFrameSavePaymentMethodRequestDto,
    IFrameSavePaymentMethodResponseDto,
    SharedPaymentIntegrationApiClient
} from 'apps/public-portal/src/app/core/api/PaymentIntegrationApiClients';

@Injectable({
    providedIn: 'root'
})
export class FiservService extends PaymentProcessorService {
    // #region PaymentProcessorService fields

    public tokenizeUrl: string;

    // #endregion

    constructor(
        private router: Router,
        sharedPaymentApi: SharedPaymentIntegrationApiClient
    ) {
        super(sharedPaymentApi);
        this.supportsTokenization = true;
    }

    // #region PaymentProcessorService

    public override async checkout() {
        this.router.navigate(['/checkout']);
    }

    public override async payCart(params): Promise<IFramePaymentResponse> {
        const request = new IFramePaymentRequestDto();
        const { paymentMethodData }: { paymentMethodData: PaymentMethodData } = params;
        if (params.authorizationConsent) request.authorizationConsent = params.authorizationConsent;
        if (params.savedPaymentMethodAuthorizationConsent) request.savedPaymentMethodAuthorizationConsent = params.savedPaymentMethodAuthorizationConsent;
        request.cartId = params.cartId;
        request.cartGuid = params.cartGuid;
        request.paymentMethodType = PaymentMethodTypeEnumConvertor.toDto(params.paymentMethodType ?? paymentMethodData.type);
        request.inboundRedirectSourceId = params.inboundRedirectSourceId;

        if (!params.saved) {
            this.mapRequest(request, paymentMethodData, params.paymentToken);
        }

        // Make the sale (Validate cart, complete payment transaction, post to teller).
        const response = await this.sharedPaymentApi.makePayment(request).toPromise();

        return new IFramePaymentResponse({
            emailAddress: response.emailAddress,
            errorMessage: response.responseMessage,
            cartStatus: response.responseSuccess
        });
    }

    public override async savePaymentMethod(params): Promise<IFrameSavePaymentMethodResponse> {
        const request = new IFrameSavePaymentMethodRequestDto();
        const { paymentMethodData }: { paymentMethodData: PaymentMethodData } = params;
        if (params.authorizationConsent) request.authorizationConsent = params.authorizationConsent;

        this.mapRequest(request, paymentMethodData, params.paymentToken);
        let response: IFrameSavePaymentMethodResponseDto;
        if (params.paymentMethodId) {
            response = await this.sharedPaymentApi.updatePaymentMethod(params.paymentMethodId, request).toPromise();
        } else {
            response = await this.sharedPaymentApi.addPaymentMethod(request).toPromise();
        }

        const saveResponse = new IFrameSavePaymentMethodResponse({
            paymentMethodId: response.paymentMethodId,
            last4: response.last4 || request.last4
        });

        // Add cardType and cardExpiry as dynamic properties since they're not in the base model
        (saveResponse as any).cardType = response.cardType || request.cardType;
        (saveResponse as any).cardExpiry = response.cardExpiry || request.cardExpiry;

        return saveResponse;
    }

    protected override mapRequest(request: IFrameSavePaymentMethodRequestDto | IFramePaymentRequestDto,
        paymentMethodData: PaymentMethodData,
        token?: string,
        testTrigger?: string) {
        // Call the base implementation first
        super.mapRequest(request, paymentMethodData, token, testTrigger);

        // Fix eCheck property mapping for Fiserv - map lowercase base properties to uppercase DTO properties
        if (paymentMethodData.type === PaymentMethodTypeEnum.ECheck) {
            (request as any).ECheckAccountNumber = (request as any).eCheckAccountNumber;
            (request as any).ECheckRoutingNumber = (request as any).eCheckRoutingNumber;
        }

        // For tokenized credit cards, ensure last4 is always extracted and passed
        if (paymentMethodData.type === PaymentMethodTypeEnum.CreditCard) {
            if (token && token.length >= 4 && !request.last4) {
                // Extract last 4 digits from token as fallback
                request.last4 = token.substring(token.length - 4);
            }
        }

        // Note: Transaction indicators (ecomind, achEntryCode) and capture flow are automatically
        // handled by the backend FiservManager based on PaymentMethodType
    }

    // #endregion
}
